import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  Tab,
  Tabs,
  Paper,
  LinearProgress,
  Stack,
  Divider,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Tooltip,
  alpha,
} from '@mui/material';
import {
  EmojiEvents as RewardIcon,
  CurrencyRupee as IncentiveIcon,
  TrendingUp,
  Stars,
  Group as TeamIcon,
  Person as SelfIcon,
  CheckCircle as CheckCircleIcon,
  MilitaryTech as RankIcon,
} from '@mui/icons-material';
import { useLocation, useHistory } from 'react-router-dom';

import RefreshButton from '../../components/RefreshButton/RefreshButton';
import { rewardService } from '../../services/reward';

const MyRewardIncentive = () => {
  const location = useLocation();
  const history = useHistory();
  const [activeTab, setActiveTab] = useState(0);
  const [rewardData, setRewardData] = useState(null);
  const [incentiveData, setIncentiveData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchRewardAchievements = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await rewardService.getMyRewardAchievements({ status: 'all' });
      if (response.status) {
        setRewardData(response);
      } else {
        setError(response.message || 'Failed to fetch reward data');
      }
    } catch (err) {
      console.error(err);
      setError(err.message || 'An error occurred while fetching data');
    } finally {
      setLoading(false);
    }
  };

  const fetchIncentiveAchievements = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await rewardService.getMyIncentiveAchievements();
      if (response.success) {
        setIncentiveData(response);
      } else {
        setError(response.message || 'Failed to fetch incentive data');
      }
    } catch (err) {
      console.error(err);
      setError(err.message || 'An error occurred while fetching data');
    } finally {
      setLoading(false);
    }
  };

  // Sync tab with URL path
  useEffect(() => {
    if (location.pathname.includes('my-incentive')) {
      setActiveTab(1);
      fetchIncentiveAchievements();
    } else {
      setActiveTab(0);
      fetchRewardAchievements();
    }
  }, [location.pathname]);

  const handleTabChange = (event, newValue) => {
    const path = newValue === 0 ? '/app/achievements/my-reward' : '/app/achievements/my-incentive';
    history.push(path);
  };

  const handleRefresh = () => {
    if (activeTab === 0) {
      fetchRewardAchievements();
    } else {
      fetchIncentiveAchievements();
    }
  };

  // Processed Reward Data from API
  const apiReward = rewardData?.extra?.summary || {};
  const rewardProgress = apiReward.progressOverview || {};

  const mappedRewardData = {
    currentRank: apiReward.activeRank || 'Associate',
    nextRank: rewardProgress.nextMilestone || 'Next Rank',
    overallProgress: rewardProgress.completionPercentage || 0,
    benefit: rewardProgress.estimatedAchievementReward?.rewardDescription || 'N/A',
    metrics: [
      {
        label: 'Self Business',
        achieved: rewardProgress.selfBusiness?.achieved || 0,
        target: rewardProgress.selfBusiness?.target || 1000000,
        color: '#1976d2'
      },
      {
        label: 'Team Business',
        achieved: rewardProgress.teamBusiness?.achieved || 0,
        target: rewardProgress.teamBusiness?.target || 4000000,
        color: '#2e7d32'
      },
    ],
    summary: [
      // { label: 'Reward Points', value: apiReward.rewardPoints?.toLocaleString('en-IN') || '0', icon: <Stars color="primary" /> },
      { label: 'Designation', value: apiReward.activeRank || 'N/A', icon: <RankIcon color="secondary" /> },
    ],
    history: rewardData?.data || [],
    upcoming: apiReward.upcomingRewards || []
  };

  // Processed Incentive Data from API
  const apiIncentive = incentiveData?.summary || {};
  const incentiveProgress = apiIncentive.progressOverview || {};

  const mappedIncentiveData = {
    currentRank: apiIncentive.activeRank || 'Associate',
    nextRank: incentiveProgress.nextMilestone || 'Next Rank',
    overallProgress: incentiveProgress.completionPercentage || 0,
    benefit: incentiveProgress.estimatedAchievementIncentive?.incentiveAmount ? `₹${incentiveProgress.estimatedAchievementIncentive.incentiveAmount.toLocaleString('en-IN')}` : 'N/A',
    metrics: [
      {
        label: 'Self Business',
        achieved: incentiveProgress.selfBusiness?.achieved || 0,
        target: incentiveProgress.selfBusiness?.target || 1000000,
        color: '#ed6c02'
      },
      {
        label: 'Team Business',
        achieved: incentiveProgress.teamBusiness?.achieved || 0,
        target: incentiveProgress.teamBusiness?.target || 3000000,
        color: '#2e7d32'
      },
    ],
    summary: [
      { label: 'Total Earned', value: `₹${(apiIncentive.totalIncentiveEarned || 0).toLocaleString('en-IN')}`, icon: <IncentiveIcon color="warning" /> },
      { label: 'Total Paid', value: `₹${(apiIncentive.totalPaidAmount || 0).toLocaleString('en-IN')}`, icon: <CheckCircleIcon color="success" /> },
      { label: 'Total Pending', value: `₹${(apiIncentive.totalPendingAmount || 0).toLocaleString('en-IN')}`, icon: <TrendingUp color="primary" /> },
    ],
    history: incentiveData?.data || []
  };

  const data = activeTab === 0 ? mappedRewardData : mappedIncentiveData;

  if (loading && ((activeTab === 0 && !rewardData) || (activeTab === 1 && !incentiveData))) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, backgroundColor: '#f4f7fe', minHeight: '100vh' }}>
      {/* Page Header */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h5" fontWeight="700" color="#2b3674">
            My Achievements
          </Typography>
        </Box>
        <RefreshButton onClick={handleRefresh} loading={loading} />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>
      )}

      {/* Tabs Selection */}
      <Paper sx={{ mb: 2, borderRadius: 2 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab icon={<RewardIcon />} iconPosition="start" label="My Reward" sx={{ py: 1.5, fontWeight: 600, minHeight: 48 }} />
          <Tab icon={<IncentiveIcon />} iconPosition="start" label="My Incentive" sx={{ py: 1.5, fontWeight: 600, minHeight: 48 }} />
        </Tabs>
      </Paper>

      {/* Summary Stats Row (Top for Incentive) */}
      {activeTab === 1 && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {mappedIncentiveData.summary.map((item, i) => (
            <Grid item xs={12} sm={4} key={i}>
              <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
                  <Avatar sx={{ bgcolor: alpha(item.icon.props.color === 'warning' ? '#ed6c02' : item.icon.props.color === 'success' ? '#2e7d32' : '#1976d2', 0.1), width: 40, height: 40 }}>
                    {React.cloneElement(item.icon, { sx: { fontSize: 20 } })}
                  </Avatar>
                  <Box>
                    <Typography variant="caption" color="textSecondary" fontWeight="600">{item.label}</Typography>
                    <Typography variant="h6" fontWeight="700">{item.value}</Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Grid container spacing={2}>
        {/* Main Content Area */}
        <Grid item xs={12} lg={activeTab === 0 ? 8 : 7}>
          <Stack spacing={2}>
            {/* Progression Overview */}
            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="700">{activeTab === 0 ? 'Reward Progression' : 'Incentive Progression'}</Typography>
                    <Typography variant="caption" color="textSecondary">Next Milestone: {data.nextRank}</Typography>
                  </Box>
                  <Chip
                    label={data.overallProgress >= 100 ? "100%+" : `${data.overallProgress}%`}
                    size="small"
                    color="primary"
                    sx={{ fontWeight: 700, borderRadius: 1.5 }}
                  />
                </Box>

                <Stack spacing={2.5}>
                  {data.metrics.map((metric, index) => {
                    const percentage = metric.target > 0 ? Math.round((metric.achieved / metric.target) * 100) : 0;
                    const isSelf = metric.label.includes('Self');
                    const otherMetric = data.metrics[index === 0 ? 1 : 0];
                    const otherPercentage = otherMetric?.target > 0 ? Math.round((otherMetric.achieved / otherMetric.target) * 100) : 0;
                    return (
                      <Box key={index}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {isSelf ? <SelfIcon fontSize="inherit" sx={{ color: metric.color }} /> : <TeamIcon fontSize="inherit" sx={{ color: metric.color }} />}
                            <Typography variant="caption" fontWeight="700">{metric.label}</Typography>
                            {isSelf && percentage >= 100 && otherPercentage < 100 && (
                              <Box component="span" sx={{ color: 'warning.dark', fontWeight: 800, fontSize: '9px', bgcolor: 'warning.light', px: 0.5, borderRadius: 0.5 }}>
                                WAITING FOR TEAM TARGET
                              </Box>
                            )}
                            {!isSelf && percentage >= 100 && otherPercentage < 100 && (
                              <Box component="span" sx={{ color: 'warning.dark', fontWeight: 800, fontSize: '9px', bgcolor: 'warning.light', px: 0.5, borderRadius: 0.5 }}>
                                WAITING FOR SELF TARGET
                              </Box>
                            )}
                          </Box>
                          <Typography variant="caption" fontWeight="800">
                            {percentage >= 100 ? "100%+" : `${percentage}%`}
                          </Typography>
                        </Box>
                      <LinearProgress
                        variant="determinate"
                        value={metric.target > 0 ? Math.min(100, (metric.achieved / metric.target) * 100) : 0}
                        sx={{ height: 6, borderRadius: 3, backgroundColor: '#eaefff', '& .MuiLinearProgress-bar': { backgroundColor: metric.color } }}
                      />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                        <Typography variant="caption" color="textSecondary" sx={{ fontSize: '10px' }}>
                          ₹{metric.achieved.toLocaleString('en-IN')}
                        </Typography>
                        <Typography variant="caption" fontWeight="600" sx={{ fontSize: '10px' }}>
                          Target: ₹{metric.target.toLocaleString('en-IN')}
                        </Typography>
                      </Box>
                    </Box>
                    );
                  })}
                </Stack>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ bgcolor: '#f8fafd', p: 1.5, borderRadius: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36 }}>
                    <TrendingUp sx={{ fontSize: 20 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="caption" fontWeight="700" color="textSecondary" sx={{ textTransform: 'uppercase', fontSize: '10px' }}>
                      {activeTab === 0 ? 'Achievement Reward' : 'Projected Bonus'}
                    </Typography>
                    <Typography variant="subtitle1" fontWeight="800" color="primary">
                      {data.benefit}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* History Table (Full Width in this column) */}
            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
              <CardHeader title={activeTab === 0 ? "Reward Milestone History" : "Installment Payout Tracking"} />
              <CardContent sx={{ p: 0 }}>
                {activeTab === 0 ? (
                  <TableContainer>
                    <Table size="small">
                      <TableHead sx={{ bgcolor: '#f9fafb' }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 800, color: '#4a5568', textTransform: 'uppercase', fontSize: '11px', letterSpacing: '0.5px' }}>Rank Milestone</TableCell>
                          <TableCell sx={{ fontWeight: 800, color: '#4a5568', textTransform: 'uppercase', fontSize: '11px', letterSpacing: '0.5px' }}>Reward Earned</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 800, color: '#4a5568', textTransform: 'uppercase', fontSize: '11px', letterSpacing: '0.5px' }}>Completed Date</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 800, color: '#4a5568', textTransform: 'uppercase', fontSize: '11px', letterSpacing: '0.5px' }}>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {mappedRewardData.history.length > 0 ? (
                          mappedRewardData.history.map((row, i) => (
                            <TableRow key={i} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                              <TableCell sx={{ py: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#2b3674' }}>{row.rankName}</Typography>
                                <Typography variant="caption" sx={{ color: '#64748b' }}>Level {row.levelCode}</Typography>
                              </TableCell>
                              <TableCell sx={{ py: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e293b' }}>{row.rewardItem}</Typography>
                                <Typography variant="caption" sx={{ color: '#64748b' }}>Value: ₹{(row.rewardAmount || 0).toLocaleString('en-IN')}</Typography>
                              </TableCell>
                              <TableCell align="right" sx={{ color: 'text.secondary', py: 2 }}>
                                {row.completedAt ? new Date(row.completedAt).toLocaleDateString() : 'N/A'}
                              </TableCell>
                              <TableCell align="right" sx={{ py: 2 }}>
                                <Box sx={{ 
                                  display: 'inline-flex', 
                                  alignItems: 'center', 
                                  gap: 1, 
                                  px: 1.5, 
                                  py: 0.5, 
                                  borderRadius: 1.5, 
                                  bgcolor: (row.status === 'completed' || row.status === 'complete' || row.status === 'approved' || row.status === 'success') ? '#f0fdf4' : (row.status === 'pending' || row.status === 'requested') ? '#fff9db' : '#fef2f2', 
                                  border: `1px solid ${(row.status === 'completed' || row.status === 'complete' || row.status === 'approved' || row.status === 'success') ? '#bcf0da' : (row.status === 'pending' || row.status === 'requested') ? '#ffe066' : '#fecaca'}` 
                                }}>
                                  <Box sx={{ 
                                    width: 6, 
                                    height: 6, 
                                    borderRadius: '50%', 
                                    bgcolor: (row.status === 'completed' || row.status === 'complete' || row.status === 'approved' || row.status === 'success') ? '#10b981' : (row.status === 'pending' || row.status === 'requested') ? '#fcc419' : '#ef4444' 
                                  }} />
                                  <Typography variant="caption" sx={{ 
                                    fontWeight: 700, 
                                    color: (row.status === 'completed' || row.status === 'complete' || row.status === 'approved' || row.status === 'success') ? '#166534' : (row.status === 'pending' || row.status === 'requested') ? '#f59f00' : '#991b1b', 
                                    textTransform: 'uppercase' 
                                  }}>
                                    {(row.status === 'completed' || row.status === 'complete' || row.status === 'approved' || row.status === 'success') ? 'Delivered' : row.status}
                                  </Typography>
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                              <Typography variant="body2" color="textSecondary">No reward achievements recorded yet.</Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead sx={{ bgcolor: '#f9fafb' }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 800, color: '#4a5568', textTransform: 'uppercase', fontSize: '11px', letterSpacing: '0.5px' }}>Installment No</TableCell>
                          <TableCell sx={{ fontWeight: 800, color: '#4a5568', textTransform: 'uppercase', fontSize: '11px', letterSpacing: '0.5px' }}>Rank Milestone</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 800, color: '#4a5568', textTransform: 'uppercase', fontSize: '11px', letterSpacing: '0.5px' }}>Cycle Payout</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 800, color: '#4a5568', textTransform: 'uppercase', fontSize: '11px', letterSpacing: '0.5px' }}>Paid</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 800, color: '#4a5568', textTransform: 'uppercase', fontSize: '11px', letterSpacing: '0.5px' }}>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {mappedIncentiveData.history.length > 0 ? (
                          mappedIncentiveData.history.map((row, i) => (
                            <TableRow key={i} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                              <TableCell sx={{ whiteSpace: 'nowrap', py: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1e293b' }}>
                                  Installment {row.cycleNo || 1} <Box component="span" sx={{ color: 'text.secondary', fontWeight: 500 }}>of {row.totalCycles || 1}</Box>
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <TrendingUp sx={{ fontSize: 12 }} /> {row.remainingCycles || 0} cycles remaining
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ py: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#2b3674' }}>{row.rankName}</Typography>
                                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500 }}>{row.payoutMonths || 0} Months Plan</Typography>
                              </TableCell>
                              <TableCell align="right" sx={{ py: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1e293b' }}>
                                  ₹{(row.payableIncentiveAmount || row.incentiveAmount || 0).toLocaleString('en-IN')}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500 }}>Per Cycle</Typography>
                              </TableCell>
                              <TableCell align="right" sx={{ color: '#10b981', fontWeight: 700, py: 2 }}>
                                ₹{(row.paidAmount || 0).toLocaleString('en-IN')}
                              </TableCell>
                              <TableCell align="right" sx={{ py: 2 }}>
                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.5, borderRadius: 1.5, bgcolor: (row.paymentStatus === 'approved' || row.paymentStatus === 'released' || row.paymentStatus === 'paid') ? '#f0fdf4' : '#fff7ed', border: `1px solid ${(row.paymentStatus === 'approved' || row.paymentStatus === 'released' || row.paymentStatus === 'paid') ? '#bcf0da' : '#ffedd5'}` }}>
                                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: (row.paymentStatus === 'approved' || row.paymentStatus === 'released' || row.paymentStatus === 'paid') ? '#10b981' : '#f59e0b' }} />
                                  <Typography variant="caption" sx={{ fontWeight: 700, color: (row.paymentStatus === 'approved' || row.paymentStatus === 'released' || row.paymentStatus === 'paid') ? '#166534' : '#9a3412', textTransform: 'uppercase' }}>
                                    {(row.paymentStatus === 'approved' || row.paymentStatus === 'released' || row.paymentStatus === 'paid') ? 'Released' : row.paymentStatus}
                                  </Typography>
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                              <Typography variant="body2" color="textSecondary">No incentive achievements recorded yet.</Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          </Stack>
        </Grid>

        {/* Sidebar Column: Stats & Roadmap */}
        <Grid item xs={12} lg={activeTab === 0 ? 4 : 5}>
          <Stack spacing={2}>
            {/* Reward Summary Cards (Only for Reward Tab) */}
            {activeTab === 0 && mappedRewardData.summary.map((item, i) => (
              <Card key={i} sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
                  <Avatar sx={{ bgcolor: '#f4f7fe', width: 40, height: 40 }}>
                    {React.cloneElement(item.icon, { sx: { fontSize: 20 } })}
                  </Avatar>
                  <Box>
                    <Typography variant="caption" color="textSecondary" fontWeight="600">{item.label}</Typography>
                    <Typography variant="h6" fontWeight="700">{item.value}</Typography>
                  </Box>
                </CardContent>
              </Card>
            ))}

            {/* Upcoming Rewards Roadmap (Only for Reward Tab) */}
            {activeTab === 0 && mappedRewardData.upcoming?.length > 0 && (
              <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: '1px solid #eef2f6' }}>
                <CardHeader title="Reward Milestone Roadmap" />
                <CardContent sx={{ pt: 1 }}>
                  <Stack spacing={2.5}>
                    {mappedRewardData.upcoming.map((milestone, idx) => (
                      <Box key={idx} sx={{ p: 2, borderRadius: 2, bgcolor: '#fff', border: '1px solid #f0f4f8' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                          <Typography variant="subtitle2" fontWeight="800" color="#2b3674">{milestone.rankName}</Typography>
                          <Typography variant="subtitle2" color="primary" fontWeight="800">{milestone.rewardItem}</Typography>
                        </Box>

                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="textSecondary" fontWeight="600">Self Business</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                              <LinearProgress
                                variant="determinate"
                                value={milestone.selfBusiness?.percentage || 0}
                                sx={{ flexGrow: 1, height: 6, borderRadius: 3, bgcolor: '#f0f0f0' }}
                              />
                              <Typography variant="caption" fontWeight="700">{milestone.selfBusiness?.percentage || 0}%</Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="textSecondary" fontWeight="600">Team Business</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                              <LinearProgress
                                variant="determinate"
                                value={milestone.teamBusiness?.percentage || 0}
                                sx={{ flexGrow: 1, height: 6, borderRadius: 3, bgcolor: '#f0f0f0', '& .MuiLinearProgress-bar': { bgcolor: '#2e7d32' } }}
                              />
                              <Typography variant="caption" fontWeight="700">{milestone.teamBusiness?.percentage || 0}%</Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            )}

            {/* Upcoming Milestones Roadmap (Only for Incentive Tab) */}
            {activeTab === 1 && apiIncentive.upcomingIncentives?.length > 0 && (
              <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: '1px solid #eef2f6' }}>
                <CardHeader title="Next Milestones Roadmap" />
                <CardContent sx={{ pt: 1 }}>
                  <Stack spacing={2.5}>
                    {apiIncentive.upcomingIncentives.map((milestone, idx) => (
                      <Box key={idx} sx={{ p: 2, borderRadius: 2, bgcolor: milestone.selfBusiness?.isComplete && milestone.teamBusiness?.isComplete ? '#f0fff4' : '#fff', border: '1px solid #f0f4f8' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                          <Typography variant="subtitle2" fontWeight="800" color="#2b3674">{milestone.rankName}</Typography>
                          <Typography variant="subtitle2" color="primary" fontWeight="800">₹{milestone.incentiveAmount.toLocaleString('en-IN')}</Typography>
                        </Box>

                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="textSecondary" fontWeight="600">Self Business</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                              <LinearProgress
                                variant="determinate"
                                value={milestone.selfBusiness?.percentage || 0}
                                sx={{ flexGrow: 1, height: 6, borderRadius: 3, bgcolor: '#f0f0f0' }}
                              />
                              <Typography variant="caption" fontWeight="700">{milestone.selfBusiness?.percentage || 0}%</Typography>
                            </Box>
                            <Typography variant="caption" color={milestone.selfBusiness?.remaining > 0 ? "error" : "success.main"} sx={{ fontSize: '10px', fontWeight: 600 }}>
                              {milestone.selfBusiness?.remaining > 0 ? `₹${milestone.selfBusiness.remaining.toLocaleString('en-IN')} left` : 'Completed'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="caption" color="textSecondary" fontWeight="600">Team Business</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                              <LinearProgress
                                variant="determinate"
                                value={milestone.teamBusiness?.percentage || 0}
                                sx={{ flexGrow: 1, height: 6, borderRadius: 3, bgcolor: '#f0f0f0', '& .MuiLinearProgress-bar': { bgcolor: '#2e7d32' } }}
                              />
                              <Typography variant="caption" fontWeight="700">{milestone.teamBusiness?.percentage || 0}%</Typography>
                            </Box>
                            <Typography variant="caption" color={milestone.teamBusiness?.remaining > 0 ? "error" : "success.main"} sx={{ fontSize: '10px', fontWeight: 600 }}>
                              {milestone.teamBusiness?.remaining > 0 ? `₹${milestone.teamBusiness.remaining.toLocaleString('en-IN')} left` : 'Completed'}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            )}
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

// Helper component for section headers in cards
const CardHeader = ({ title }) => (
  <Box sx={{ px: 3, pt: 3, pb: 1 }}>
    <Typography variant="subtitle1" fontWeight="700">{title}</Typography>
  </Box>
);

export default MyRewardIncentive;
